// Big editorial card for the latest / featured post.
import { Link } from "react-router";

import { AuthorAvatar } from "./AuthorAvatar";
import { BlogCover } from "./BlogCover";
import type { BlogPost } from "./posts";

type FeaturedPostProps = {
  post: BlogPost;
};

const cardClassName =
  "featured-card relative grid grid-cols-1 items-center gap-[clamp(28px,4vw,56px)] overflow-hidden rounded-[24px] border border-grindigo-100 bg-white p-[clamp(24px,3vw,36px)] no-underline shadow-[0_30px_60px_-32px_rgba(99,102,241,0.25)] transition-[transform,box-shadow,border-color] duration-[250ms] ease-[ease] hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-[0_40px_80px_-28px_rgba(99,102,241,0.35)] min-[960px]:grid-cols-[1.05fr_1fr]";

function normalizeInternalPath(href: string) {
  return href.replace(/\/$/, "") || "/";
}

export function FeaturedPost({ post }: FeaturedPostProps) {
  const body = <Body post={post} />;
  const inner = (
    <section className="px-[clamp(24px,5vw,80px)] pt-4 pb-[clamp(48px,6vw,80px)]">
      <div className="mx-auto max-w-[1180px]">
        {post.href ? (
          <Link to={normalizeInternalPath(post.href)} className={cardClassName}>
            {body}
          </Link>
        ) : (
          <div className={cardClassName}>{body}</div>
        )}
      </div>
    </section>
  );

  return inner;
}

function Body({ post }: FeaturedPostProps) {
  return (
    <>
      <BlogCover
        tint={post.tint}
        mascot={post.mascot}
        height={360}
        imageSrc={post.coverImageSrc}
        imageSrcMobile={post.coverImageSrcMobile}
        imagePosition={post.coverImagePosition}
        imageAlt={post.title}
        coverArt={post.coverArt}
      />

      <div>
        <div className="mb-4 flex flex-wrap items-center gap-2.5">
          <span className="text-xs text-secondary">{post.dateLabel}</span>
        </div>

        <h2 className="font-companion mb-4 text-[clamp(28px,3.4vw,44px)] font-bold leading-[1.08] tracking-[-0.025em] text-primary [text-wrap:balance]">
          {post.title}
        </h2>

        <p className="mb-6 text-base leading-[1.6] text-secondary [text-wrap:pretty]">
          {post.dek}
        </p>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AuthorAvatar author={post.author} mascot={post.mascot} size={36} />
            <div className="text-[13px] leading-[1.3]">
              <div className="font-semibold text-primary">
                {post.author.name}
              </div>
              {post.author.role && (
                <div className="text-xs text-secondary">{post.author.role}</div>
              )}
            </div>
          </div>
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600">
            Read the story
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 12h14M13 5l7 7-7 7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
      </div>
    </>
  );
}
